import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import BoxIcon from '@sdk/shifts/components/BoxIcon';
import EventBoxContainerStyle, {
  EventBoxContainerProps,
} from '@sdk/schedule/components/EventBoxContainerStyle';
import EventBoxOuter from '@sdk/schedule/components/EventBoxOuter';
import EventBoxInner from '@sdk/schedule/components/EventBoxInner';
import {
  EventBoxCommonBaseProps,
  EventBoxSelectableProps,
} from '@sdk/schedule/types/EventBoxProps';
import { SickCalloutType } from '@sdk/noShows/types/SickCalloutType';

import SickCalloutIcon from '../assets/svg/Icons/24/sick-white.svg';

import type { OpenEventCallback } from './types';
import FlexRow from './ui/FlexRow';
import EventBoxSelect from './components/EventBoxSelect';
import getAbsenceColors from './helpers/getAbsenceColors';
import getRenderBoxLine from './helpers/getRenderBoxLine';

type BoxProps = {
  bgHover: string;
  borderColorHover: string;
} & EventBoxContainerProps;

const Box = styled(FlexRow)<BoxProps>`
  ${EventBoxContainerStyle}

  background-color: ${({ bg }): string => bg};
  border-color: ${({ borderColor }): string => borderColor};

  ${EventBoxOuter}:hover & {
    background-color: ${({ bgHover }): string => bgHover};
    border-color: ${({ borderColorHover }): string => borderColorHover};
  }
`;

type Props = {
  sickCallout: SickCalloutType;
  openEvent: OpenEventCallback;
  isHovered: boolean;
} & EventBoxCommonBaseProps &
  EventBoxSelectableProps;

const SickCalloutBox: React.FC<React.PropsWithChildren<Props>> = ({
  hoverComponent,
  sickCallout,
  openEvent,
  shiftAppearanceMode,
  isSelected,
  showSelect,
  toggleSelection,
  height,
  showPlus,
  isHovered,
}) => {
  const { colors } = useTheme();
  const shiftAppearance = sickCallout.shiftAppearance?.[shiftAppearanceMode];
  const { bg, bgHover, borderColor, borderColorHover, textColor, textColorHover } =
    getAbsenceColors(sickCallout, colors);
  const { firstLine, secondLine, thirdLine, fourthLine } = shiftAppearance || {};

  const onClick = (evt: React.MouseEvent<HTMLDivElement>): void => {
    openEvent({ event: sickCallout, isExchange: false, $event: evt });
  };

  const renderBoxLine = getRenderBoxLine({
    color: textColor,
    hoverColor: textColorHover,
    hoverComponent: EventBoxOuter,
  });

  return (
    <EventBoxOuter>
      <Box
        hoverComponent={hoverComponent}
        bg={bg}
        bgHover={bgHover}
        borderColor={borderColor}
        borderColorHover={borderColorHover}
        showPlus={showPlus}
        onClick={onClick}
        height={height}>
        {(isHovered || isSelected || showSelect) &&
          sickCallout.canBeSelected &&
          !sickCallout.foreign && (
            <EventBoxSelect
              isSelected={isSelected}
              toggleSelection={toggleSelection}
              showSelect={showSelect}
              hoverComponent={EventBoxOuter}
            />
          )}
        <EventBoxInner>
          {renderBoxLine(firstLine, true)}
          {secondLine && renderBoxLine(secondLine)}
          {thirdLine && renderBoxLine(thirdLine)}
          {fourthLine && renderBoxLine(fourthLine)}
        </EventBoxInner>
        <BoxIcon source={SickCalloutIcon} color={colors.white} />
      </Box>
    </EventBoxOuter>
  );
};

export default SickCalloutBox;
